/* eslint-disable no-shadow */
/* eslint-disable import/no-cycle */
import {
  ActionTree, Module, MutationTree, GetterTree
} from 'vuex';

import { vehicleSearchService } from '@/services/vehicle-search-service';
import SearchQuery from '@/models/SearchQuery';
import IVehicle from '@/models/vehicle/interfaces/vehicle.d';
import IEnquiry from '@/models/interfaces/IEnquiry';
import ExtraData from '@/models/ExtraData';
import { enquiryService } from '@/services/EnquiryService';
import PolicyDetails from '@/models/PolicyDetails';
import IManufacturer from '@/models/vehicle/interfaces/manufacturer.d';
import IRange from '@/models/vehicle/interfaces/range.d';
import IDerivative from '@/models/vehicle/interfaces/derivative.d';
import VehicleLevel from '@/enums/VehicleLevel';
import SelectedTimeSlot from '@/models/date-picker/SelectedTimeSlot';
import ExtraDataUtils from '@/utils/ExtraDataUtils';
import IRetailerVehicleAvailability from '@/models/test-drive/interfaces/retailer-vehicle-availability.d';
import PostEnquiryState from '../states/post-enquiry-state';
import IPostEnquiryState from '../states/interfaces/post-enquiry-state.d';
import IRootState from '../states/interfaces/index.d';

export const state: IPostEnquiryState = new PostEnquiryState();

export const getters: GetterTree<IPostEnquiryState, IRootState> = {
  getEnquiryId: (state) => state.enquiryId,
  vehicles: (state) => state.enquiryVehicles,
  policyDetails: (state) => state.policyDetails,
  derivatives: (state) => state.derivatives
};

export const mutations: MutationTree<IPostEnquiryState> = {
  setEnquiryId: (state: PostEnquiryState, enquiryId: number) => {
    state.enquiryId = enquiryId;
  },
  setEnquiryReferenceCode: (state: PostEnquiryState, referenceCode: string) => {
    state.enquiryReferenceCode = referenceCode;
  },
  setEnquiryVehicles: (state: PostEnquiryState, vehicles: IVehicle[]) => {
    state.enquiryVehicles = vehicles;
  },
  setPolicyDetails: (state: PostEnquiryState, policyDetails: PolicyDetails[]) => {
    state.policyDetails = policyDetails;
  },
  setManufacturers: (state, manufacturers: IManufacturer[] | null) => {
    state.manufacturers = manufacturers;
  },
  setRanges: (state, ranges: IRange[] | null) => {
    state.ranges = ranges;
  },
  setDerivatives: (state, derivatives: IDerivative[] | null) => {
    state.derivatives = derivatives;
  }
};

export const actions: ActionTree<IPostEnquiryState, IRootState> = {
  retrieveManufacturer({ commit, dispatch, rootState }, vehicle: IVehicle) {
    if (vehicle && vehicle.manufacturerId && vehicle.manufacturerId > 0) {
      const searchQuery = new SearchQuery();
      searchQuery.manufacturerIds = [vehicle.manufacturerId];
      searchQuery.vehicleType = vehicle.vehicleType;
      vehicleSearchService.fetchManufacturers(searchQuery)
        .then((manufacturers: IManufacturer[]) => {
          if (manufacturers !== null) {
            commit('setManufacturers', manufacturers);
            dispatch('vehicleModule/submitManufacturer', manufacturers[0], { root: true });
          }
          dispatch('retrieveRange', vehicle);
        });
    }
  },
  retrieveRange({ commit, dispatch, rootState }, vehicle: IVehicle) {
    const searchQuery = new SearchQuery();
    searchQuery.rangeIds = vehicle.rangeId;
    searchQuery.vehicleType = vehicle.vehicleType;
    vehicleSearchService.fetchRanges(searchQuery)
      .then((ranges: IRange[]) => {
        if (ranges !== null) {
          commit('setRanges', ranges);
          dispatch('vehicleModule/submitRange', ranges[0], { root: true });
        }

        dispatch('searchQueryModule/submitVehicleType', vehicle.vehicleType, { root: true });
        dispatch('vehicleModule/submitVehicleType', vehicle.vehicleType, { root: true });
        dispatch('vehicleModule/submitVehicleLevel', vehicle.vehicleLevel, { root: true });

        if (vehicle.vehicleLevel === VehicleLevel.Range
          || vehicle.vehicleLevel === VehicleLevel.Model) {
          dispatch('vehicleModule/submitVehicleDataSet', true, { root: true });
        } else {
          dispatch('retrieveDerivative', vehicle);
        }
      });
  },
  async retrieveDerivative({ commit, dispatch, rootGetters }, vehicle: IVehicle) {
    // Gets all derivatives for specified manufacturer / range.
    const searchQuery = new SearchQuery();
    searchQuery.manufacturerIds = [vehicle.manufacturerId];
    searchQuery.rangeIds = vehicle.rangeId;
    searchQuery.vehicleType = vehicle.vehicleType;

    const results = await vehicleSearchService.fetchResults(searchQuery);
    if (results) {
      commit('setDerivatives', results.items);

      const derivative: IDerivative | undefined = results.items.find((x) => x.derivativeId === vehicle.derivativeId);

      if (derivative) {
        dispatch('vehicleModule/submitDerivative', derivative, { root: true });

        await dispatch('populateSearchQuery', derivative);
      }

      if (!rootGetters['vehicleModule/averagePriceAvailable']) {
        dispatch('vehicleModule/submitAveragePercentSaving', null, { root: true });
      }
    }
  },
  async populateSearchQuery({ dispatch }, derivative: IDerivative) {
    dispatch('searchQueryModule/submitBodystyles', derivative.bodystyles, { root: true });
    dispatch('searchQueryModule/submitDoors', derivative.doors, { root: true });
    dispatch('searchQueryModule/submitFuelTypes', [derivative.fuelTypeName], { root: true });
    dispatch('searchQueryModule/submitManufacturerIds', [derivative.manufacturerId], { root: true });
    dispatch('searchQueryModule/submitRangeId', derivative.rangeId, { root: true });
    dispatch('searchQueryModule/submitTransmission', derivative.transmissionName, { root: true });
    dispatch('searchQueryModule/submitTrims', [derivative.trimName], { root: true });
    await dispatch('vehicleSearchModule/updateResults', null, { root: true });
    await dispatch('vehicleSearchModule/updateFilteredResults', null, { root: true });
    dispatch('vehicleModule/submitVehicleDataSet', true, { root: true });
  },
  submitEnquiryData({ dispatch, commit }, enquiry: IEnquiry) {
    dispatch('personalDetailsModule/submitFirstName',
      enquiry.firstName,
      { root: true });
    dispatch('personalDetailsModule/submitLastName',
      enquiry.surname,
      { root: true });
    dispatch('personalDetailsModule/submitEmail',
      enquiry.email,
      { root: true });
    dispatch('personalDetailsModule/submitTelephone',
      enquiry.telephone,
      { root: true });

    commit('setEnquiryId', enquiry.enquiryId);
    commit('setEnquiryReferenceCode', enquiry.enquiryReferenceCode);

    enquiry.extraData.forEach((extraData) => {
      dispatch('enquiryModule/submitExtraData', extraData, { root: true });
    });
  },
  submitEnquiryVehicles({ commit }, vehicles: IVehicle[]) {
    commit('setEnquiryVehicles', vehicles);
  },
  submitEnquiryExtraData: async ({ dispatch, commit, state }, extraData: ExtraData[]) => {
    extraData.forEach((ed) => {
      dispatch('enquiryModule/submitExtraData', ed, { root: true });
    });

    if (state.enquiryReferenceCode) {
      await enquiryService.updateEnquiryExtraDataByReferenceCode(extraData, state.enquiryReferenceCode);
    } else {
      await enquiryService.updateEnquiryExtraData(extraData, state.enquiryId);
    }
  },
  submitPolicyDetails({ commit }, policyDetails: PolicyDetails[]) {
    commit('setPolicyDetails', policyDetails);
  },
  submitRetailerSelectionDetails({ commit }, enquiry: IEnquiry) {
    const retailerId = ExtraDataUtils.getValue(enquiry.extraData, 'RetailerId');
    const testDriveVehicleId = ExtraDataUtils.getValue(enquiry.extraData, 'testDriveVehicleId');
    const retailerName = ExtraDataUtils.getValue(enquiry.extraData, 'RetailerName');
    const retailerAddressLine1 = ExtraDataUtils.getValue(enquiry.extraData, 'RetailerAddressLine1');
    const retailerAddressTown = ExtraDataUtils.getValue(enquiry.extraData, 'RetailerAddressTown');
    const retailerAddressCounty = ExtraDataUtils.getValue(enquiry.extraData, 'RetailerAddressCounty');
    const retailerAddressPostcode = ExtraDataUtils.getValue(enquiry.extraData, 'RetailerAddressPostcode');
    const retailerDistanceInMiles = ExtraDataUtils.getValue(enquiry.extraData, 'RetailerDistanceInMiles');

    if (!retailerId) {
      return;
    }

    const testDriveVehicleIdNumber = testDriveVehicleId ? Number.parseInt(testDriveVehicleId, 10) : 0;
    const retailer: IRetailerVehicleAvailability = {
      testDriveVehicleId: testDriveVehicleIdNumber,
      retailerId: Number.parseInt(retailerId, 10),
      retailerName: retailerName || '',
      address: {
        addressLine1: retailerAddressLine1 || '',
        town: retailerAddressTown || '',
        county: retailerAddressCounty || '',
        postCode: retailerAddressPostcode || ''
      },
      distanceInMiles: retailerDistanceInMiles ? Number.parseInt(retailerDistanceInMiles, 10) : 0,
      location: {
        latitude: 0,
        longitude: 0
      },
      availability: []
    };

    commit('retailerModule/setSelectedRetailer', retailer, { root: true });
  },
  submitTestDriveAppointmentDetails({ commit }, enquiry: IEnquiry) {
    const appointmentDate = ExtraDataUtils.getValue(enquiry.extraData, 'RetailerAppointmentDateTime');
    const appointmentDateFormatted = ExtraDataUtils.getValue(enquiry.extraData, 'RetailerAppointmentDateFormatted');
    const appointmentStartTimeFormatted = ExtraDataUtils.getValue(enquiry.extraData, 'RetailerAppointmentStartTimeFormatted');
    const appointmentEndTimeFormatted = ExtraDataUtils.getValue(enquiry.extraData, 'RetailerAppointmentEndTimeFormatted');
    const appointmentStartEndFormatted = ExtraDataUtils.getValue(enquiry.extraData, 'RetailerAppointmentStartEndFormatted');

    if (!appointmentDate) {
      return;
    }

    const appointment: SelectedTimeSlot = new SelectedTimeSlot(
      1,
      new Date(appointmentDate),
      new Date(appointmentDate),
      appointmentDateFormatted || '',
      appointmentStartTimeFormatted || '',
      appointmentEndTimeFormatted || '',
      appointmentStartEndFormatted || ''
    );

    commit('testDriveAvailabilityModule/setAppointment', appointment, { root: true });
  }
};

export const postEnquiryModule: Module<IPostEnquiryState, IRootState> = {
  state,
  actions,
  mutations,
  getters,
  namespaced: true
};
